'use client';

import { useCallback, useState } from 'react';
import { FiEye, FiPhone, FiMail, FiCheckCircle, FiX } from 'react-icons/fi';
import { useFeatureQuota } from '@/lib/hooks/useFeatureQuota';
import { setContactQuota, refreshContactQuota } from '@/lib/hooks/useContactQuota';
import UnlockPaywall from '@/components/UnlockPaywall';

interface ContactViewButtonProps {
  contactApplicationId: number;
  className?: string;
  onContactViewed?: (contactInfo: { name: string; mobile: string; email?: string }) => void;
  alreadyViewed?: boolean;
  /** Known not to answer — the paywall says so before taking the money. */
  unreachable?: boolean;
}

interface ContactInfo {
  name: string;
  mobile: string;
  email?: string;
}

interface QuotaInfo {
  used: number;
  limit: number;
  remaining: number;
  isExceeded: boolean;
}

export default function ContactViewButton({
  contactApplicationId,
  className = '',
  onContactViewed,
  alreadyViewed = false,
  unreachable = false
}: ContactViewButtonProps) {
  const { viewContact, loading } = useFeatureQuota();

  const [showModal, setShowModal] = useState(false);
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [quotaInfo, setQuotaInfo] = useState<QuotaInfo | null>(null);
  const [blockingError, setBlockingError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  // A user with quota left never sees the paywall — the reveal (and the quota charge)
  // happens straight away. For a number we know does not answer, that would spend a
  // credit with no warning anywhere, so this one screen stands in front of it.
  const [confirmingRisk, setConfirmingRisk] = useState(false);

  const revealContact = useCallback(async () => {
    const result = await viewContact(contactApplicationId);

    if (result.quotaInfo) {
      setContactQuota({
        used: result.quotaInfo.used,
        limit: result.quotaInfo.limit,
        remaining: result.quotaInfo.remaining,
      });
    }

    if (result.success && result.canView && result.contactInfo) {
      setContactInfo(result.contactInfo);
      setQuotaInfo(result.quotaInfo || null);
      setBlockingError(null);
      onContactViewed?.(result.contactInfo);
      return true;
    }

    setContactInfo(null);
    setQuotaInfo(result.quotaInfo || null);
    // A 403 is the paywall. Being signed out lands on the paywall too — it offers a
    // login button. Anything else is a real error worth showing.
    const isAuthError = result.error === 'User not authenticated';
    setBlockingError(
      result.success || isAuthError ? null : result.error || 'Something went wrong. Please try again.'
    );
    return false;
  }, [contactApplicationId, onContactViewed, viewContact]);

  const proceedToReveal = useCallback(async () => {
    setConfirmingRisk(false);
    setChecking(true);
    try {
      await revealContact();
    } finally {
      setChecking(false);
    }
  }, [revealContact]);

  const handleViewContact = async () => {
    setBlockingError(null);
    setShowModal(true);
    // Already paid for once — re-opening it costs nothing, so don't nag.
    if (unreachable && !alreadyViewed && !contactInfo) {
      setConfirmingRisk(true);
      return;
    }
    await proceedToReveal();
  };

  const handleShareWhatsApp = (mobile: string) => {
    const message = `Hi! I found you through mutual transfer matching. I'm interested in discussing a potential transfer. Please contact me if interested.`;
    const whatsappUrl = `https://wa.me/91${mobile}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const closeModal = () => {
    setShowModal(false);
    setConfirmingRisk(false);
  };

  return (
    <>
      <button
        onClick={handleViewContact}
        disabled={loading}
        className={`flex items-center gap-2 whitespace-nowrap px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      >
        {loading ? (
          <>
            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            Loading...
          </>
        ) : contactInfo || alreadyViewed ? (
          <>
            <FiCheckCircle size={14} />
            {alreadyViewed ? 'See Again' : 'Viewed'}
          </>
        ) : (
          <>
            <FiEye size={14} />
            View Contact
          </>
        )}
      </button>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#101B2D]/40 p-4 backdrop-blur-sm">
          <div className="relative w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-[0_24px_60px_-24px_rgba(16,27,45,0.6)] ring-1 ring-[#101B2D]/10">
            <button
              onClick={closeModal}
              aria-label="Close"
              className="absolute right-3 top-3 z-10 rounded-full p-1.5 text-[#101B2D]/40 transition-colors hover:bg-[#101B2D]/5 hover:text-[#101B2D]"
            >
              <FiX size={16} />
            </button>

            {confirmingRisk ? (
              /* Risk gate — stands between an unreachable contact and the quota charge */
              <div className="p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-700">
                  Unreachable
                </p>
                <h3 className="mt-2 text-base font-semibold leading-tight text-[#101B2D]">
                  This number has never replied to us
                </h3>
                <p lang="hi" className="mt-1 text-sm text-[#101B2D]/55">
                  इस नंबर ने हमें कभी जवाब नहीं दिया
                </p>
                <p className="mt-3 text-sm text-[#101B2D]/75">
                  Opening it will still use one of your contacts. Unlock at your own risk.
                </p>
                <p lang="hi" className="mt-1 text-sm text-[#101B2D]/50">
                  खोलने पर आपका एक संपर्क खर्च होगा। अपने जोखिम पर अनलॉक करें।
                </p>
                <button
                  onClick={proceedToReveal}
                  className="mt-4 flex h-11 w-full items-center justify-center rounded-xl bg-[#F0A020] text-sm font-bold text-[#101B2D] transition-colors hover:bg-[#DE9012]"
                >
                  Unlock anyway
                </button>
                <button
                  onClick={closeModal}
                  className="mt-2 h-9 w-full text-xs font-medium text-[#101B2D]/50 transition-colors hover:text-[#101B2D]"
                >
                  Go back
                </button>
              </div>
            ) : checking ? (
              <div className="flex h-40 flex-col items-center justify-center gap-3">
                <div className="h-7 w-7 animate-spin rounded-full border-2 border-[#101B2D]/15 border-t-[#101B2D]" />
                <p className="text-xs text-[#101B2D]/50">Checking your access…</p>
              </div>
            ) : contactInfo ? (
              /* Unlocked — the thing they actually came for */
              <div className="p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#101B2D]/55">Contact details</p>
                <p lang="hi" className="mt-0.5 text-xs text-[#101B2D]/45">संपर्क जानकारी</p>

                <div className="mt-4 space-y-3">
                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-wide text-[#101B2D]/45">Name</p>
                    <p className="text-base font-semibold text-[#101B2D]">{contactInfo.name}</p>
                  </div>

                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-wide text-[#101B2D]/45">Mobile</p>
                    <div className="mt-0.5 flex items-center gap-2">
                      <a href={`tel:${contactInfo.mobile}`} className="font-mono text-lg font-semibold text-[#101B2D]">
                        {contactInfo.mobile}
                      </a>
                      <button
                        onClick={() => handleShareWhatsApp(contactInfo.mobile)}
                        className="flex items-center gap-1 rounded-lg bg-[#0E9F6E] px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-[#0B7F58]"
                      >
                        <FiPhone size={12} />
                        WhatsApp
                      </button>
                    </div>
                  </div>

                  {contactInfo.email && (
                    <div>
                      <p className="text-[11px] font-medium uppercase tracking-wide text-[#101B2D]/45">Email</p>
                      <div className="mt-0.5 flex items-center gap-2">
                        <p className="truncate text-sm font-medium text-[#101B2D]">{contactInfo.email}</p>
                        <a
                          href={`mailto:${contactInfo.email}`}
                          className="flex flex-shrink-0 items-center gap-1 rounded-lg bg-[#101B2D] px-2.5 py-1 text-xs font-semibold text-white"
                        >
                          <FiMail size={12} />
                          Email
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {quotaInfo && quotaInfo.limit > 0 && (
                  <p className="mt-4 border-t border-[#101B2D]/10 pt-3 text-xs text-[#101B2D]/55">
                    {quotaInfo.remaining} of {quotaInfo.limit} contacts left
                    <span lang="hi" className="ml-1 text-[#101B2D]/40">· {quotaInfo.limit} में से {quotaInfo.remaining} बाकी</span>
                  </p>
                )}
              </div>
            ) : blockingError ? (
              /* A real failure — not the paywall */
              <div className="p-5">
                <h3 className="text-base font-semibold text-[#101B2D]">Couldn&apos;t open this contact</h3>
                <p lang="hi" className="mt-0.5 text-sm text-[#101B2D]/50">संपर्क नहीं खुल सका</p>
                <p className="mt-3 text-sm text-[#101B2D]/70">{blockingError}</p>
                <button
                  onClick={handleViewContact}
                  className="mt-4 flex h-11 w-full items-center justify-center rounded-xl bg-[#101B2D] text-sm font-semibold text-white"
                >
                  Try again
                </button>
              </div>
            ) : (
              /* Paywall — pay right here, no page hops */
              <UnlockPaywall
                kind="contacts"
                quotaInfo={quotaInfo}
                unreachableWarning={unreachable}
                onUnlocked={async () => {
                  await refreshContactQuota();
                  await revealContact();
                }}
                onDismiss={closeModal}
                unlockingLabel="Opening your contact…"
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}
